import { Injectable, NgZone } from '@angular/core';
import { decryptData, encryptData } from "../encryptData/encrypt-data.service";
import swal from 'sweetalert2';
import { Subject, throwError } from "rxjs";
import { Router } from "@angular/router";
import { CommonConstants } from "../constants/common-constants";
import * as CryptoJS from 'crypto-js';
import { AbstractControl, UntypedFormControl, ValidatorFn } from '@angular/forms';
import { PermissionListService } from './permission-list.service';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  private readonly datePipe = new DatePipe('en-US');
  pageName = new Subject<any>();
  grnPageName = new Subject<any>();
  adjustmentClose = new Subject<any>();
  grnBackBtn = new Subject<any>();
  backBtn = new Subject<any>();
  notificationSub = new Subject<any>();
  isInCall = new Subject<any>();
  rejectCall = new Subject<any>();
  currentUser: any;
  constructor(
    private _router: Router,
    private _permissionService: PermissionListService,
    private _zone: NgZone
  ) { }

  /**
   * Summary: This method is used to set value in localstorage.
   * @param keyName :  input is the name of key
   * @param keyValue :  input is value of key
   * @returns
   */
  public setLocalStorageData(keyName: any, keyValue: any) {
    localStorage.setItem(keyName, <string>encryptData(keyValue));
    return true
  }

  /**
   * Summary: This method is used to get value from localstorage.
   * @param keyName :  input is the name of key
   * @returns
   */
  public getLocalStorageData(keyName: any) {
    var localStorageDetails = localStorage.getItem(keyName);
    if (localStorageDetails != null && localStorageDetails != '') {
      return decryptData(localStorageDetails)
    } else {
      return false
    }
  }

  /*public encryptLocalStorageData(value: any) {
    if (value != null && value !== '') {
      const dataToEncrypt = typeof value === 'string' ? value : JSON.stringify(value);
      const encryptedValue: any = encryptData(dataToEncrypt);
      return encryptedValue;
    } else {
      return false;
    }
  }*/

  public encryptLocalStorageData(value: any) {
    if (value != null && value !== '') {
      const dataToEncrypt = typeof value === 'string' ? value : JSON.stringify(value);
      let encryptedValue: any = encryptData(dataToEncrypt);

      // Make it URL/file safe by replacing special characters
      encryptedValue = encryptedValue
        .replace(/\+/g, '-')   // replace + with -
        .replace(/\//g, '_')   // replace / with _
        .replace(/=+$/, '');   // remove = padding

      return encryptedValue;
    } else {
      return false;
    }
  }

  public decryptLocalStorageData(encryptedValue: string) {
    if (!encryptedValue) return false;

    // Restore original Base64
    let base64 = encryptedValue
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    // Add padding back if needed
    while (base64.length % 4) {
      base64 += '=';
    }

    return decryptData(base64);
  }

  /*decryptResponse(response :any ) {
    try {
      return CryptoJS.AES.encrypt(JSON.stringify(response), CommonConstants.encryptSecretKey).toString();
    } catch (error: any) {
      console.error('Decryption failed', error);
      return null;
    }
  }*/

  decryptResponse(cipherText: string): any {
    try {
      const bytes = CryptoJS.AES.decrypt(cipherText, CommonConstants.encryptSecretKey);
      const decryptedText = bytes.toString(CryptoJS.enc.Utf8);

      if (!decryptedText) {
        throw new Error('Decryption returned empty string. Possibly wrong key or corrupted input.');
      }

      return JSON.parse(decryptedText);
    } catch (error: any) {
      console.error('Decryption failed:', error.message || error);
      return null;
    }
  }

  /**
   * Summary: This method is used to remove value from localstorage.
   * @param keyName : input is the name of key
   * @returns
   */
  public removeLocalStorageData(keyName: any) {
    localStorage.removeItem(keyName);
  }


  clearLocalStorageData() {
    this.removeLocalStorageData(CommonConstants.USER_DATA)
    this.removeLocalStorageData(CommonConstants.TOKEN)
    this.removeLocalStorageData(CommonConstants.IS_ADMIN)
    this.removeLocalStorageData(CommonConstants.FORGOT_EMAIL)
    this.removeLocalStorageData(CommonConstants.DEVICE_TOKEN)
    this.removeLocalStorageData(CommonConstants.DEFAULT_ROUTE)
  }

  //Session storage functions
  setSessionData(key: string, data: any): void {
    try {
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(data),
        CommonConstants.encryptSecretKey
      ).toString();
      sessionStorage.setItem(key, encryptedData);
    } catch (error) {
      console.error('Session encryption failed', error);
    }
  }

  getSessionData(key: string): any {
    try {
      const encryptedData = sessionStorage.getItem(key);
      if (!encryptedData) {
        return null;
      }
      const bytes = CryptoJS.AES.decrypt(encryptedData, CommonConstants.encryptSecretKey);
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (error) {
      console.error('Session decryption failed', error);
      return null;
    }
  }

  /**
   * Toast message
   * @param message
   * @param icon
   */
  toast(message: any, icon: any) {
    let Toast = swal.mixin({
      toast: true,
      position: 'bottom-right',
      // iconColor: 'white',
      customClass: {
        popup: 'colored-toast'
      },
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: false
    })
    return Toast.fire({
      icon: icon,
      title: message
    })
  }

  getNotificationRedirectUrl(payload: any): string | null {
    if (!payload) return null;
    
    // Support all possible formats (flat data, nested data, unified wrapper)
    const data = payload.data || payload.payload || payload || {};
    
    // 1. Try to find the camelCase notificationType from standard properties
    let notificationType = payload.notificationType || data.notificationType || data.type;
    
    // 2. If notificationType is missing, detect it from key properties inside data + title keywords
    if (!notificationType) {
      const title = payload.notification?.title || payload.title || '';
      const titleClean = title.toLowerCase().replace(/\s+/g, '');
      
      if (data.workOrderRequestId) {
        if (titleClean.includes('approved')) {
          notificationType = 'workOrderApproved';
        } else if (titleClean.includes('rejected')) {
          notificationType = 'workOrderRejected';
        } else {
          notificationType = 'workOrderRequest';
        }
      } else if (data.collectionNoteId) {
        if (titleClean.includes('received')) {
          notificationType = 'collectionNoteReceived';
        } else {
          notificationType = 'collectionNoteCreated';
        }
      } else if (data.purchaseOrderId) {
        notificationType = 'purchaseOrder';
      } else if (data.inboundId) {
        notificationType = 'inBound';
      } else if (data.transferProjectId) {
        if (titleClean.includes('status')) {
          notificationType = 'updateProjectStatus';
        } else {
          notificationType = 'projectTransferRequest';
        }
      } else if (data.grnId) {
        if (titleClean.includes('final')) {
          notificationType = 'finalGRN';
        } else {
          notificationType = 'preliminaryGRN';
        }
      } else if (data.transferId) {
        notificationType = 'itemTransfer';
      } else if (data.assembleItemId) {
        if (titleClean.includes('stored')) {
          notificationType = 'assembleStatusStored';
        } else {
          notificationType = 'assembleStatusCreated';
        }
      } else if (data.cycleCountId) {
        notificationType = 'cycleCountCreated';
      } else if (titleClean) {
        notificationType = title;
      }
    }
    
    if (!notificationType) return null;
    
    // Construct unified object format expected by getNotificationUrl
    const unifiedNotification = {
      notificationType: notificationType,
      payload: {
        collectionNoteId: data.collectionNoteId,
        collectionNoteStatus: data.collectionNoteStatus || data.status || '',
        workOrderRequestId: data.workOrderRequestId,
        purchaseOrderId: data.purchaseOrderId,
        inboundId: data.inboundId,
        transferProjectId: data.transferProjectId,
        grnId: data.grnId,
        transferId: data.transferId,
        assembleItemId: data.assembleItemId,
        cycleCountId: data.cycleCountId
      }
    };
    
    let url = this.getNotificationUrl(unifiedNotification);
    if (url && !url.startsWith('/')) {
      url = '/' + url;
    }
    return url;
  }

  getNotificationUrl(notification: any): string | null {
    if (!notification) return null;
    const userRole = this.getLocalStorageData('_UR');
    
    switch (notification.notificationType) {
      case 'collectionNoteDispatched':
      case 'collectionNoteScheduledForPickup':
      case 'collectionNoteCollected':
      case 'collectionNoteCreated':
      case 'collectionNoteInProgress':
      case 'collectionNoteStarted':
      case 'collectionNotePrepared':
      case 'collectionNotePickupRequested':
      case 'collectionNoteApproved':
        return `/collection-note/edit/${notification?.payload?.collectionNoteId}/${notification?.payload?.collectionNoteStatus}`;

      case 'collectionNoteReceived':
        return `/collection-note/view/${notification?.payload?.collectionNoteId}`;

      case 'workOrderApproved':
      case 'workOrderRejected':
        return userRole == 'vendor' ? `/work-order-management/my-work-orders/view-my-work-order/${notification?.payload?.workOrderRequestId}` : `/work-order-management/work-order-request/view-work-order-request/${notification?.payload?.workOrderRequestId}`;

      case 'purchaseOrder':
        return userRole == 'warehousePersonnel' ? `/purchase-orders/view-purchase-order/${notification?.payload?.purchaseOrderId}` : userRole == 'suppliers' ? `/purchase-orders/add-edit-purchase-order/${notification?.payload?.purchaseOrderId}` : `/purchase-orders/add-edit-purchase-order/${notification?.payload?.purchaseOrderId}`;

      case 'inBound':
        return userRole == 'warehousePersonnel' || userRole == 'suppliers' ? `/inbound-management/view-inbound/${notification?.payload?.inboundId}` : `/inbound-management/add-edit-inbound/${notification?.payload?.inboundId}`;

      case 'projectTransferRequest':
      case 'updateProjectStatus':
        return `/project-management/project-transfer-request/view-project-transfer-request/${notification?.payload?.transferProjectId}`;

      case 'preliminaryGRN':
        return userRole == 'warehousePersonnel' ? `/preliminary-grn-management/view-grn/${notification?.payload?.grnId}` : `/preliminary-grn-management/add-edit-grn/${notification?.payload?.grnId}`;

      case 'finalGRN':
        return userRole == 'warehousePersonnel' ? `/final-grn-management/view-grn/${notification?.payload?.grnId}` : `/final-grn-management/add-edit-grn/${notification?.payload?.grnId}`;

      case 'vendorInventory':
        return `/my-inventory/add-edit-inventory`;

      case 'itemTransfer':
        return `/transfer-order-management/view-transfer-order/${notification?.payload?.transferId}`;

      case 'workOrderRequest':
        return userRole == 'vendor' ? `/work-order-management/my-work-orders/view-my-work-order/${notification?.payload?.workOrderRequestId}` : `/work-order-management/work-order-request/add-edit-work-order-request/${notification?.payload?.workOrderRequestId}`;

      case 'assembleStatusCreated':
      case 'assembleStatusAccepted':
      case 'assembleStatusAssigned':
      case 'assembleStatusCollected':
      case 'assembleStatusReady':
      case 'assembleStatusAssembled':
        return `/assembled-items-requests/add-edit/${notification?.payload?.assembleItemId}`;

      case 'assembleStatusStored':
        return `/assembled-items-requests/view/${notification?.payload?.assembleItemId}`;

      case 'cycleCountCreated':
        return `cycle-count/add-edit-cycle-count/${notification?.payload?.cycleCountId}`;
      default:
        console.warn('Unknown notification type', notification.notificationType);
        return null;
    }
  }

  showNotificationPopup(title: any, body: any, payload?: any) {
    const baseHref = typeof document !== 'undefined' ? (document.querySelector('base')?.getAttribute('href') || '/') : '/';
    const normalizedBase = baseHref.startsWith('/') ? baseHref : '/' + baseHref;
    const logoUrl = `${window.location.origin}${normalizedBase}images/logo.png`;
    
    // Inject custom styles if not already present
    if (typeof document !== 'undefined' && !document.getElementById('custom-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'custom-notification-styles';
      style.innerHTML = `
        .custom-premium-notification-popup {
          z-index: 999999 !important;
          margin-top: 85px !important;
          margin-right: 15px !important;
          padding: 14px 18px !important;
          border-radius: 16px !important;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          border: 1px solid #e2e8f0 !important;
          width: 380px !important;
          background-color: #ffffff !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    let Toast = swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
      background: '#ffffff',
      customClass: {
        popup: 'custom-premium-notification-popup'
      },
      didOpen: (popup) => {
        popup.addEventListener('click', (event) => {
          console.log('[FCM Swal] Notification card clicked!');
          event.preventDefault();
          event.stopPropagation();
          const url = this.getNotificationRedirectUrl(payload);
          console.log('[FCM Swal] Calculated Redirect URL:', url);
          if (url) {
            swal.close();
            this.navigateByUrl(url);
          } else {
            console.warn('[FCM Swal] Could not determine URL for payload:', payload);
          }
        });
        popup.style.cursor = 'pointer';
      }
    });

    return Toast.fire({
      html: `
        <div style="display: flex; align-items: flex-start; gap: 14px; font-family: 'Roboto', sans-serif; text-align: left;">
          <div style="position: relative; flex-shrink: 0;">
            <img src="${logoUrl}" style="width: 46px; height: 46px; border-radius: 10px; object-fit: contain; border: 1px solid #f1f5f9; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);" alt="Logo" />
            <span style="position: absolute; bottom: -2px; right: -2px; width: 12px; height: 12px; background-color: #3b82f6; border: 2.5px solid #ffffff; border-radius: 50%;"></span>
          </div>
          <div style="flex: 1; min-width: 0; padding-top: 2px;">
            <div style="font-weight: 600; font-size: 14.5px; color: #0f172a; margin-bottom: 3px; line-height: 1.2;">${title}</div>
            <div style="font-size: 13px; color: #475569; line-height: 1.45; font-weight: 400; word-break: break-word;">${body}</div>
          </div>
        </div>
      `
    });
  }

  navigateByUrl(url: string) {
    console.log('[FCM Swal] navigateByUrl executing navigation to:', url);
    this._zone.run(() => {
      this._router.navigateByUrl('loading', { skipLocationChange: true }).then(() => {
        console.log('[FCM Swal] Successfully bypassed loading, now navigating to:', url);
        this._router.navigateByUrl(url, { replaceUrl: true });
      }).catch((err) => {
        console.error('[FCM Swal] Navigation bypass failed:', err);
      });
    });
  }
  removedFilters() {
    this.removeLocalStorageData(CommonConstants.IS_BACK);
    this.removeLocalStorageData(CommonConstants.PAGE_NAME);
    this.removeLocalStorageData(CommonConstants.PER_PAGE);
    this.removeLocalStorageData(CommonConstants.CURRENT_PAGE);
    this.removeLocalStorageData(CommonConstants.USER_STATUS);
    this.removeLocalStorageData(CommonConstants.IS_SEARCH);
    this.removeLocalStorageData(CommonConstants.USER_STATUS);
    this.removeLocalStorageData(CommonConstants.FORGOT_EMAIL);
    this.removeLocalStorageData(CommonConstants.QUERY);
  }

  /**
   * This method is use for get permission list for all module
   * @param route 
   * @returns 
   */
  //   getPermissions(route: any) {
  //     let currentPermission: any
  //     let permissionList = this._permissionService?.getPermissionList();
  //     if(permissionList){
  //       currentPermission = permissionList?.find(
  //         (p: any) => p.value === route
  //       );
  //     }

  //     return currentPermission
  // }
  getPermissions(route: string): any {
    const permissionList = this._permissionService?.getPermissionList();
    if (!permissionList) return null;

    // Recursive search
    const findPermission = (permissions: any[], route: string): any => {
      for (const perm of permissions) {
        if (perm.value === route) {
          return perm;
        }

        if (perm.childPermissions && perm.childPermissions.length) {
          const found = findPermission(perm.childPermissions, route);
          if (found) return found;
        }
      }
      return null;
    };

    return findPermission(permissionList, route);
  }


  /**
   * Summary: This method is use for handle error
   * @param err
   * @private
   */
  public handleAuthError(err: any, showErrorToast: boolean = true): any {
    const errorMessage = err?.error?.message || err?.message || "Error";

    if (err.status === 0 || err?.error?.statusCode === 0) {
      if (showErrorToast) {
        this.toast(err?.meta?.message || "Error", "error");
      }
    }
    if (err.status === 400 || err?.error?.statusCode === 400) {
      if (showErrorToast) {
        this.toast(errorMessage, "error");
      }
    }
    if (err.status === 404 || err?.error?.statusCode === 404) {
      if (showErrorToast) {
        this.toast(errorMessage, "error");
      }
    }
    if (err.status === 401 || err?.error?.statusCode === 401) {
      if (showErrorToast) {
        this.toast(errorMessage, "error");
      }
      this.getUnauthorizedUser();
    }
    if (err.status === 500 || err?.error?.statusCode === 500) {
      if (showErrorToast) {
        this.toast(errorMessage, "error");
      }
    }
    return throwError(err);
  }

  getUnauthorizedUser() {
    this._router.navigateByUrl('/login');
    this.removeLocalStorageData(CommonConstants.TOKEN);
    this.removeLocalStorageData(CommonConstants.IS_ADMIN);
  }

  /**
     * Summary: It is use for handle error of image
     * @param e
     * @param type
     */
  setErrorImage(e: any, type?: any) {
    if (type == 'user') {
      e.target.src = 'images/profile.png';
    } else {
      e.target.src = 'images/placeHolderImage.jpg';
    }
  }

  public checkLogin(keyName: any) {
    return !!localStorage.getItem(keyName)
  }

  allowOnlyNumbers(event: any) {
    const charCode = event.charCode;
    if (charCode !== 0 && charCode !== 8 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
    }
  }

  // In your component.ts
  allowOnlyNumbersAndDot(event: KeyboardEvent, allowNegative: boolean = false) {
    const input = event.target as HTMLInputElement;
    const char = event.key;

    const controlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Escape',
      'Enter',
    ];

    // Allow control keys
    if (controlKeys.includes(char)) {
      return;
    }

    // Allow numbers 0-9
    if (/^[0-9]$/.test(char)) {
      return;
    }

    // Allow single dot (not as first character)
    if (char === '.' && !input.value.includes('.') && input.value.length > 0) {
      return;
    }

    // Allow negative sign if enabled
    if (
      allowNegative &&
      char === '-' &&
      input.selectionStart === 0
    ) {
      return;
    }

    // Block everything else
    event.preventDefault();
  }


  validate0To100(event: any) {
    const input = event.target as HTMLInputElement;
    const pos = input.selectionStart!;
    let v = input.value;

    if (v !== "0" && v.startsWith("0") && !v.startsWith("0.")) {
      v = v.replace(/^0+/, "");
    }

    if (v === "") v = "0";

    if (v.endsWith(".")) {
      input.value = v;
      input.setSelectionRange(pos, pos);
      return;
    }

    const num = parseFloat(v);

    if (isNaN(num) || num < 0 || num > 100) v = "0";

    input.value = v;
    input.setSelectionRange(pos, pos);
  }



  allowTextOnly(event: any) {
    let input = event.target;
    let value = input.value;

    // Remove numbers and special characters (allow only letters and spaces)
    value = value.replace(/[^a-zA-Z ]/g, '');

    // Remove leading space
    value = value.replace(/^\s+/g, '');

    // Replace multiple spaces with a single space
    value = value.replace(/\s{2,}/g, ' ');

    input.value = value;
  }

  exportSerialNumbersToExcel(serialNumbers: (string | number)[], fileName = 'serial_numbers.xlsx'): void {
    if (!serialNumbers || serialNumbers.length === 0) {
      console.error('No data to export');
      return;
    }

    // Convert array to Excel-friendly format
    const worksheetData = serialNumbers.map(value => ({
      serialNumber: value
    }));

    // Create worksheet
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(worksheetData);

    // Create workbook
    const workbook: XLSX.WorkBook = {
      Sheets: { Sheet1: worksheet },
      SheetNames: ['Sheet1']
    };

    // Export file
    XLSX.writeFile(workbook, fileName);
  }

  maxQuantityValidator = (max: number): ValidatorFn => {
    return (control: AbstractControl) => {
      const value = control.value;
      if (value != null && value > max) {
        return { maxQuantityExceeded: true };
      }
      return null;
    };
  };

  generateXlsx(exportData: any, name: string) {
    this.currentUser = this.getLocalStorageData('_UR');

    if (exportData?.length > 0) {
      // Filter out the keys dynamically
      const cleanedData = exportData.map((item: any) => {
        const newItem = { ...item };
        if (this.currentUser == 'warehouseManager' && name == 'Purchase Order') {
          ['Price', 'Currency'].forEach(key => delete newItem[key]);
        }
        ['Created At', 'Updated At'].forEach(key => {
          if (newItem[key]) {
            newItem[key] = this.datePipe.transform(
              newItem[key],
              'dd MMM, yyyy hh:mm a'
            );
          }
        });
        return newItem;
      });

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(cleanedData);
      const workbook: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBuffer: any = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      this.saveAsExcelFile(excelBuffer, name);
    } else {
      this.toast(`There is no ${name} data`, 'error');
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(data, `${fileName}.xlsx`);
  }

  replaceSpaceWithUnderscore(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cursorPos = input.selectionStart ?? 0;
    const prevChar = value[cursorPos - 1];
    const nextChar = value[cursorPos];

    // Block ALL special chars except underscore
    const allowedPattern = /^[a-zA-Z0-9_]$/;

    // Handle space → underscore
    if (event.key === ' ') {
      event.preventDefault();
      if (prevChar === '_' || nextChar === '_') return;

      const start = cursorPos;
      const end = input.selectionEnd ?? cursorPos;

      input.value = value.slice(0, start) + '_' + value.slice(end);
      input.setSelectionRange(start + 1, start + 1);
      return;
    }

    // If key is NOT a letter, number, or underscore → block it
    if (!allowedPattern.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Prevent consecutive underscores
    if (event.key === '_' && (prevChar === '_' || nextChar === '_')) {
      event.preventDefault();
      return;
    }
  }




  generatePdf(data: any[], name: string) {
    if (!data || data.length === 0) {
      this.toast(`There is no ${name} data`, 'error')
      return;
    }
    this.currentUser = this.getLocalStorageData('_UR');

    // Filter out the keys dynamically
    const cleanedData = data.map((item: any) => {
      const newItem = { ...item };
      if (this.currentUser == 'warehouseManager' && name == 'Purchase Order') {
        ['Price', 'Currency'].forEach(key => delete newItem[key]);
      }
      ['Created At', 'Updated At'].forEach(key => {
        if (newItem[key]) {
          newItem[key] = this.datePipe.transform(
            newItem[key],
            'dd MMM, yyyy hh:mm a'
          );
        }
      });
      return newItem;
    });
    const headers = Object.keys(cleanedData[0]);
    const tableBody: any[] = [];
    tableBody.push(headers.map((h) => ({ text: h, style: "tableHeader" })));
    cleanedData.forEach((item) => {
      const row = headers.map((h: any) => {
        let val = item[h];

        if (val === null || val === undefined || val === "") return "-";
        const valStr = String(val);
        if (valStr.includes("T") && !isNaN(Date.parse(valStr))) {
          return new Date(valStr).toLocaleString(); // format to local time
        }

        return val;
      });

      tableBody.push(row);
    });
    // 🔹 Define PDF structure
    setTimeout(() => {
      const docDefinition: any = {
        pageOrientation: "landscape",
        pageSize: "A3",
        content: [
          {
            text: `${name} Report`,
            style: "header",
            margin: [0, 0, 0, 10],
          },
          {
            table: {
              headerRows: 1,
              widths: Array(headers.length).fill("*"),
              body: tableBody,
            },
            layout: "lightHorizontalLines",
          },
        ],
        styles: {
          header: { fontSize: 18, bold: true, alignment: "center" },
          tableHeader: {
            bold: true,
            fillColor: "#eeeeee",
          },
        },
        defaultStyle: { fontSize: 9 },
      };

      (pdfMake as any)
        .createPdf(docDefinition, (pdfFonts as any).pdfMake?.vfs)
        .download(`${name}.pdf`);
    }, 1000);
  }

  /**
   * Prevents the default action for Enter key presses to restrict accidental button clicks.
   * @param event The keyboard event.
   */
  preventEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

}


export class noWhitespaceValidation {
  static noWhitespaceValidator(control: UntypedFormControl): any {
    const isWhitespace = (control && control.value && control.value.toString() || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { required: true };
  }


}

export class requiredNonZero {
  static requiredNonZeroValidator(control: any): any {
    const value = control?.value;

    // invalid if: null, undefined, empty, only whitespace, OR 0
    const isInvalid =
      value === null ||
      value === undefined ||
      value <= 0 ||
      value.toString().trim().length === 0;

    return isInvalid ? { required: true } : null;
  }
}
